import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import Navbar from "../../components/Navbar";
import { publicRequest } from "../../requestMethods";
import { follow, unFollow, logout } from "../../redux/userRedux";
import theme from "../../theme";
import { mobile } from "../../responsive";
import { IconUser, IconDocument, IconFolder, IconLogOut, IconUsers } from "../../icons";
import ActivityWidget from "../../components/ActivityWidget";
import communityImg from "../../assets/community.png";
import ltceLogo from "../../assets/ltce.png";

/* ── Shell ── */
const Shell = styled.div`
  display: flex; min-height: 100vh; background: ${theme.bg};
`;

/* ── Sidebar (same as Home/Folders) ── */
const Sidebar = styled.aside`
  width: ${theme.sidebarWidth}; flex-shrink: 0; background: ${theme.sidebarBg};
  border-right: 1px solid ${theme.separator}; display: flex; flex-direction: column;
  position: fixed; top: 0; left: 0; height: 100vh; z-index: 60;
  ${mobile(`display: none;`)}
`;
const SidebarTop = styled.div`padding: 22px 18px 16px; border-bottom: 1px solid ${theme.separator};`;
const LogoRow = styled(Link)`display: flex; align-items: center; gap: 10px; text-decoration: none;`;
const LogoMark = styled.div`
  width: 36px; height: 36px; background: ${theme.primary}; color: #fff;
  border-radius: 9px; display: flex; align-items: center; justify-content: center;
  font-size: 17px; font-weight: 800; font-family: Georgia, serif; flex-shrink: 0;
`;
const LogoText = styled.div`font-size: 14px; font-weight: 700; color: ${theme.textMain}; line-height: 1.2;`;
const Nav = styled.nav`padding: 14px 10px 6px; display: flex; flex-direction: column; gap: 2px;`;
const SidebarMiddle = styled.div`
  flex: 1; display: flex; flex-direction: column; justify-content: center;
  overflow: hidden; min-height: 0;
`;
const NavItem = styled(Link)`
  display: flex; align-items: center; gap: 10px; padding: 9px 12px; border-radius: 8px;
  font-size: 13px; font-weight: 500; color: ${theme.textMuted}; background: transparent;
  text-decoration: none; transition: background 0.15s, color 0.15s;
  &:hover { background: ${theme.primaryLight}; color: ${theme.primary}; }
`;
const CommunityMainNavItem = styled(NavItem)`justify-content: flex-start;`;
const CommunityChevron = styled.span`
  width: 7px;
  height: 7px;
  display: inline-block;
  border-right: 1.8px solid currentColor;
  border-bottom: 1.8px solid currentColor;
  transform: ${(p) => (p.$open ? "rotate(-135deg)" : "rotate(45deg)")};
  margin-top: ${(p) => (p.$open ? "2px" : "-1px")};
  transition: transform 0.15s ease, margin-top 0.15s ease;
`;
const CommunityChevronHit = styled.span`
  margin-left: auto;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  &:hover { background: ${theme.primaryLight}; }
`;
const CommunitySubList = styled.div`
  margin: 4px 0 4px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
const CommunitySubItem = styled.button`
  width: 100%;
  border: none;
  border-radius: 8px;
  background: ${(p) => (p.$active ? theme.primaryLight : "transparent")};
  color: ${(p) => (p.$active ? theme.primary : theme.textMuted)};
  padding: 7px 10px;
  font-size: 12px;
  font-weight: ${(p) => (p.$active ? "700" : "600")};
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;
  transition: background 0.15s, color 0.15s;
  &:hover { background: ${theme.primaryLight}; color: ${theme.primary}; }
`;
const CommunitySubAvatar = styled.img`
  width: 18px;
  height: 18px;
  border-radius: 5px;
  object-fit: cover;
  flex-shrink: 0;
`;
const NewNoteBtn = styled.button`
  margin: 0 10px 14px; display: flex; align-items: center; justify-content: center; gap: 8px;
  background: ${theme.primary}; color: #fff; border: none; border-radius: 10px;
  padding: 11px 0; font-size: 13px; font-weight: 700; font-family: inherit; cursor: pointer;
  transition: background 0.15s; &:hover { background: ${theme.primaryDark}; }
`;
const SidebarUser = styled.div`padding: 14px 16px; border-top: 1px solid ${theme.separator}; display: flex; align-items: center; gap: 10px;`;
const UserAvatar = styled.div`
  width: 34px; height: 34px; border-radius: 50%; background: ${theme.bgAlt};
  border: 1.5px solid ${theme.cardBorder}; overflow: hidden; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 700; color: ${theme.primary}; cursor: pointer;
`;
const UserAvatarImg = styled.img`width: 100%; height: 100%; object-fit: cover;`;
const UserInfo = styled.div`flex: 1; min-width: 0;`;
const UserName = styled.div`font-size: 12.5px; font-weight: 700; color: ${theme.textMain}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
const UserRole = styled.div`font-size: 10px; font-weight: 600; color: ${theme.textMuted}; text-transform: uppercase; letter-spacing: 0.06em;`;
const LogoutBtn = styled.button`
  background: transparent; border: none; padding: 4px; cursor: pointer; color: ${theme.danger};
  display: flex; align-items: center; border-radius: 6px; flex-shrink: 0; opacity: 0.7;
  transition: opacity 0.15s, background 0.15s;
  &:hover { opacity: 1; background: ${theme.dangerLight}; }
`;

/* ── Main ── */
const Main = styled.div`
  margin-left: ${theme.sidebarWidth}; flex: 1; display: flex; flex-direction: column; min-width: 0;
  ${mobile(`margin-left: 0;`)}
`;

const Content = styled.main`
  padding: 32px 32px 80px;
  ${mobile(`padding: 20px 14px 80px;`)}
`;

const Heading = styled.h1`
  font-size: 22px; font-weight: 800; color: ${theme.textMain};
  margin-bottom: 24px; letter-spacing: -0.3px;
`;

/* ── User card ── */
const UserCard = styled.div`
  background: ${theme.cardBg};
  border-radius: 12px;
  border: 1.5px solid ${theme.cardBorder};
  box-shadow: 0 1px 6px rgba(26, 18, 8, 0.05);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
  transition: box-shadow 0.15s;
  &:hover { box-shadow: 0 4px 16px rgba(26, 18, 8, 0.09); }
  ${mobile(`padding: 14px; gap: 12px;`)}
`;

const Avatar = styled.div`
  width: 46px; height: 46px; border-radius: 50%;
  border: 1.5px solid ${theme.cardBorder}; background: ${theme.bgAlt};
  overflow: hidden; flex-shrink: 0; display: flex; align-items: center; justify-content: center;
  font-size: 18px; color: ${theme.textMuted};
`;

const AvatarImg = styled.img`width: 100%; height: 100%; object-fit: cover;`;

const UserInfoLink = styled(Link)`
  flex: 1; text-decoration: none; display: flex; flex-direction: column; gap: 2px;
`;

const Username = styled.span`
  font-size: 14px; font-weight: 700; color: ${theme.textMain};
  transition: color 0.15s;
  ${UserInfoLink}:hover & { color: ${theme.primary}; }
`;

const Institution = styled.span`font-size: 12px; color: ${theme.textMuted};`;

const FollowButton = styled.button`
  background: ${(p) => (p.$following ? theme.bgAlt : theme.primary)};
  color: ${(p) => (p.$following ? theme.textMuted : "#fff")};
  border: 1.5px solid ${(p) => (p.$following ? theme.cardBorder : theme.primary)};
  border-radius: 8px; padding: 7px 20px; font-size: 12.5px; font-weight: 700;
  font-family: inherit; cursor: pointer; flex-shrink: 0; transition: all 0.15s;
  &:hover:not(:disabled) { background: ${(p) => (p.$following ? theme.bgAlt : theme.primaryDark)}; transform: translateY(-1px); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const StatusText = styled.p`
  font-size: 14px; color: ${(p) => (p.$error ? theme.danger : theme.textMuted)};
  text-align: center; padding: 40px 0;
`;

export default function Search() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const currentUser = useSelector((s) => s.user.currentUser);
  const searchedValue = useSelector((s) => s.user.searchedValue);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [followLoading, setFollowLoading] = useState({});
  const [communities, setCommunities] = useState([]);
  const [myCommunity, setMyCommunity] = useState(null);
  const [communityMenuOpen, setCommunityMenuOpen] = useState(false);

  const STATIC_URL = (process.env.REACT_APP_BASE_URL || "http://localhost:8000/api").replace("/api", "");
  const avatarSrc = currentUser?.profilePicture ? `${STATIC_URL}/images/${currentUser.profilePicture}` : null;

  useEffect(() => {
    if (!searchedValue) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    publicRequest.get(`/users/findusers/${encodeURIComponent(searchedValue)}`)
      .then((res) => { if (!cancelled) setUsers(res.data); })
      .catch(() => { if (!cancelled) setError("Failed to fetch users."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [searchedValue]);

  useEffect(() => {
    const run = async () => {
      try {
        const r = await publicRequest.get("/community");
        setCommunities(r.data || []);
      } catch {
        setCommunities([]);
      }
    };
    run();
  }, []);

  useEffect(() => {
    if (!currentUser?.institution) return;
    publicRequest.get(`/community/institution/${encodeURIComponent(currentUser.institution)}`)
      .then((r) => {
        setMyCommunity(r.data);
        setCommunities((prev) => prev.find((c) => c._id === r.data._id) ? prev : [r.data, ...prev]);
      })
      .catch(() => {});
  }, [currentUser]);

  const handleFollow = async (targetId) => {
    if (!currentUser || followLoading[targetId]) return;
    const isFollowing = currentUser.followings?.includes(targetId);
    setFollowLoading((p) => ({ ...p, [targetId]: true }));
    try {
      if (isFollowing) {
        await publicRequest.put(`/users/${targetId}/unfollow`, { userId: currentUser._id });
        dispatch(unFollow(targetId));
      } else {
        await publicRequest.put(`/users/${targetId}/follow`, { userId: currentUser._id });
        dispatch(follow(targetId));
      }
    } catch { /* silent */ }
    finally { setFollowLoading((p) => ({ ...p, [targetId]: false })); }
  };

  const isMember = (community) => community?.members?.some((m) => m === currentUser?._id || m?._id === currentUser?._id || m?.toString() === currentUser?._id);
  const isLTCECommunity = (community) => {
    const target = `${community?.name || ""} ${community?.institution || ""}`.toUpperCase();
    return target.includes("LTCE");
  };
  const getCommunityLogo = (community) => isLTCECommunity(community) ? ltceLogo : communityImg;
  const communityNavList = (() => {
    const items = [];
    if (myCommunity) items.push(myCommunity);
    communities.forEach((c) => {
      if (isMember(c) && !items.find((x) => x._id === c._id)) items.push(c);
    });
    return items;
  })();

  return (
    <Shell>
      {/* Sidebar */}
      <Sidebar>
        <SidebarTop>
          <LogoRow to="/">
            <LogoMark>N</LogoMark>
            <div><LogoText>NotesHub</LogoText></div>
          </LogoRow>
        </SidebarTop>
        <Nav>
          <NavItem to="/"><IconDocument size="15px" /> Feed</NavItem>
          <NavItem to="/folders"><IconFolder size="15px" /> Folders</NavItem>
          <CommunityMainNavItem to="/community" $active={location.pathname.startsWith("/community")}>
            <IconUsers size="15px" /> Community
            <CommunityChevronHit
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCommunityMenuOpen((v) => !v);
              }}
              aria-label="Toggle my communities"
              title="Toggle my communities"
            >
              <CommunityChevron $open={communityMenuOpen} />
            </CommunityChevronHit>
          </CommunityMainNavItem>
          {communityMenuOpen && (
            <CommunitySubList>
              {communityNavList.length === 0 ? (
                <CommunitySubItem type="button" disabled style={{ opacity: 0.6, cursor: "default" }}>
                  No communities yet
                </CommunitySubItem>
              ) : communityNavList.map((c) => (
                <CommunitySubItem key={c._id} type="button" onClick={() => navigate("/community")}>
                  <CommunitySubAvatar src={getCommunityLogo(c)} alt={`Logo - ${c.institution || c.name}`} />
                  {c.name}
                </CommunitySubItem>
              ))}
            </CommunitySubList>
          )}
        </Nav>
        <SidebarMiddle>
          <ActivityWidget />
        </SidebarMiddle>
        <NewNoteBtn onClick={() => navigate("/")}>
          + New Note
        </NewNoteBtn>
        {currentUser && (
          <SidebarUser>
            <UserAvatar onClick={() => navigate(`/profile/${currentUser._id}`)}>
              {avatarSrc ? <UserAvatarImg src={avatarSrc} alt={currentUser.username} /> : currentUser.username?.[0]?.toUpperCase()}
            </UserAvatar>
            <UserInfo>
              <UserName>{currentUser.username}</UserName>
              <UserRole>Student</UserRole>
            </UserInfo>
            <LogoutBtn onClick={() => dispatch(logout())} title="Sign out">
              <IconLogOut size="15px" />
            </LogoutBtn>
          </SidebarUser>
        )}
      </Sidebar>

      {/* Main */}
      <Main>
        <Navbar />
        <Content>
          <Heading>
            {searchedValue ? `Results for "${searchedValue}"` : "Search Users"}
          </Heading>

          {!searchedValue ? (
            <StatusText>Use the search bar above to find users.</StatusText>
          ) : loading ? (
            <StatusText>Searching…</StatusText>
          ) : error ? (
            <StatusText $error>{error}</StatusText>
          ) : users.length === 0 ? (
            <StatusText>No users found for "{searchedValue}".</StatusText>
          ) : (
            users.map((user) => {
              const isFollowing = currentUser?.followings?.includes(user._id);
              const isOwnProfile = currentUser?._id === user._id;
              const uAvatarSrc = user.profilePicture ? `${STATIC_URL}/images/${user.profilePicture}` : null;
              return (
                <UserCard key={user._id}>
                  <Avatar>
                    {uAvatarSrc ? <AvatarImg src={uAvatarSrc} alt={user.username} /> : <IconUser size="1.4em" />}
                  </Avatar>
                  <UserInfoLink to={`/profile/${user._id}`}>
                    <Username>{user.username}</Username>
                    {user.institution && <Institution>{user.institution}</Institution>}
                  </UserInfoLink>
                  {!isOwnProfile && (
                    <FollowButton
                      $following={isFollowing}
                      onClick={() => handleFollow(user._id)}
                      disabled={!!followLoading[user._id]}
                    >
                      {followLoading[user._id] ? "…" : isFollowing ? "Unfollow" : "Follow"}
                    </FollowButton>
                  )}
                </UserCard>
              );
            })
          )}
        </Content>
      </Main>
    </Shell>
  );
}
